/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */
import { camelCase, filter, flow, get, isBoolean, map } from 'lodash';
import { parseISO, format } from 'date-fns';
import { DEFAULT_ERROR_MESSAGE, DATE_FORMAT } from './constants';

import { UserRole } from '@/constants/Users';
import { customAttributeTypeToFieldType } from '@/graphql/utils/custom_attributes/typeNames';
import peopleQuery from '@/graphql/new_object/dropdown_options/PersonOptions.gql';
import groupQuery from '@/graphql/new_object/dropdown_options/GroupOptions.gql';

import {
  AttributesGroup,
  FieldsGroup,
  FormField,
  Mapper,
  ObjectAttribute,
  RawObjectAttribute,
  RawValidOption,
  RawZenObject,
  ValidationOptions,
  ValidOption,
  ZenObject,
} from './types';

import { groupDropdownOptionsQueryFilter, personDropdownOptionsQueryFilter } from './queryFilter';

import * as validators from './validators';

interface DefaultValidatorErrorMessages {
  [key: string]: (...args: any[]) => string;
}

const defaultValidatorErrorMessages: DefaultValidatorErrorMessages = {
  alpha: (): string => 'This value accepts only alphabet characters.',
  complex: (): string => 'This is an invalid value.',
  decimal: (): string => 'This value should be a decimal.',
  email: (): string => 'This value should be a valid email address.',
  integer: (): string => 'This value should be an integer',
  ipAddress: (): string => 'This value should be a valid IP address.',
  maxLength: (maxLength: string | number): string => `This value must be at most ${maxLength} characters long.`,
  maxValue: (maxValue: string | number): string => `“This value should be less than or equal to ${maxValue}.`,
  minLength: (minLength: string | number): string => `This field must be ${minLength} characters or more.`,
  minValue: (minValue: string | number): string => `This value should be greater than or equal to ${minValue}.`,
  numeric: (): string => 'This value accepts only numeric characters.',
  regex: (): string => `This value does not meet its associated RegEx restriction.`,
  required: (): string => 'This is a mandatory field.',
  url: (): string => 'This value should be a valid URL address.',
  validValues: (): string => 'This is an invalid value.',
};

export const getErrorMessage = (validations: any[], validationInfo: any): string => {
  const firstError = validations && validations.find(validation => !validationInfo[camelCase(validation.validator)]);
  if (firstError) {
    const defaultErrorMessageFunction = defaultValidatorErrorMessages[camelCase(firstError.validator)];
    if (defaultErrorMessageFunction) {
      return defaultErrorMessageFunction(...firstError.meta);
    }
  }
  return DEFAULT_ERROR_MESSAGE;
};

export const addFieldToSortedFieldGroup = (
  sortedFieldGroup: FieldsGroup[],
  field: FormField,
  fieldGroupIndex: number,
  groupedAttrOrder: AttributesGroup[]
) => {
  // Get the field group associated to the passed index
  let currentFieldGroup = sortedFieldGroup[fieldGroupIndex];

  // Get the defined order for the Object attribute
  const sortedAttributeGroup = groupedAttrOrder[fieldGroupIndex];

  // Crete the field group if it does noe exist
  if (!currentFieldGroup) {
    currentFieldGroup = {
      name: sortedAttributeGroup.name,
      label: sortedAttributeGroup.label,
      expanded: isBoolean(sortedAttributeGroup.expanded) ? sortedAttributeGroup.expanded : true,
      groupFields: [],
    };
  }
  if (field.isCustomAttribute) {
    // just push the field
    currentFieldGroup.groupFields.push(field);
  } else {
    // Get the attr order and add the field to the same position
    const sortedAttrIndex = sortedAttributeGroup.attributes.indexOf(field.name);
    currentFieldGroup.groupFields[sortedAttrIndex] = field;
  }

  // Crete a copy of the sorted field group and replace old current group with
  // the new one
  const copyOfSortedFieldGroup = sortedFieldGroup.slice();
  copyOfSortedFieldGroup[fieldGroupIndex] = currentFieldGroup;

  return copyOfSortedFieldGroup;
};

export const serializeFormData = (formData: any, mappedFormFields: Map<string, FormField>) => {
  // TODO: Hardcoded way to define which the recurrence fields are. Not very dynamic.
  const recurrenceFieldsNames = [
    'period',
    'repeatInterval',
    'days',
    'dueIn',
    'lastDayOfMonth',
    'repeatTimes',
    'stopOn',
  ];

  return Object.keys(formData).reduce(
    (serialized, fieldName) => {
      const field = mappedFormFields.get(fieldName);

      // temporary check until we decide how to serialize repeatOption
      if (!field || fieldName === 'repeatOption' || fieldName === 'repeatOn') return serialized;

      const attrMapperKey = field.isCustomAttribute ? field.customAttributeType : fieldName;

      const submissionDataParser = get(field, 'mapper.extraInfo.submissionDataParser', null);
      let parsedData;
      if (submissionDataParser) {
        parsedData = submissionDataParser(formData[fieldName]);
      } else {
        parsedData = formData[fieldName];
      }
      if (parsedData === undefined) return serialized;

      if (field.isCustomAttribute) {
        const type = customAttributeTypeToFieldType[attrMapperKey];
        const serializedCustomAttribute = {
          name: fieldName,
          [type]: {
            value: parsedData,
          },
        };
        const customAttributes = serialized.customAttributes.concat(serializedCustomAttribute);
        return { ...serialized, customAttributes };
      }
      if (recurrenceFieldsNames.includes(fieldName)) {
        const recurrenceInfo = { ...serialized.regularAttributes.recurrenceInfo, [fieldName]: parsedData };
        const regularAttributes = { ...serialized.regularAttributes, recurrenceInfo };
        return { ...serialized, regularAttributes };
      }

      const regularAttributes = { ...serialized.regularAttributes, [fieldName]: parsedData };
      return { ...serialized, regularAttributes };
    },
    { regularAttributes: {}, customAttributes: [] }
  );
};

export function flatByAttribute(attribute) {
  return fieldValue => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.map(value => value[attribute]);
    }
    return fieldValue ? fieldValue[attribute] : fieldValue;
  };
}

export const personDropdownExtraInfo = {
  optionsQuery: peopleQuery,
  optionsQueryFilter: personDropdownOptionsQueryFilter,
  dropdownOptionsParser: data => {
    return get(data, 'dropdownOptions.edges', []).map(edge => ({
      value: edge.node.email.value,
      label: edge.node.name.value || edge.node.email.value,
      role: edge.node.userRole.value,
      id: edge.node.id,
      $isDisabled: edge.node.userRole.value === UserRole.Reader,
    }));
  },
  editValueParser: (data, multiple) => {
    // Parsing initial value for non-multiple attrs
    if (!multiple) {
      const val = get(data, 'value');
      if (val) {
        return {
          value: val.email.value,
          label: val.name.value,
          role: val.userRole.value,
          id: val.id,
          $isDisabled: val.userRole.value === UserRole.Reader,
        };
      } else {
        return null;
      }
    }

    return get(data, 'value.edges', []).map(edge => ({
      value: edge.node.email.value,
      label: edge.node.name.value,
      role: edge.node.userRole.value,
      id: edge.node.id,
      $isDisabled: edge.node.userRole.value === UserRole.Reader,
    }));
  },
  submissionDataParser: flatByAttribute('id'),
};

// Groups

const groupHasPeople = connection => connection.node.people.value.totalCount > 0;
const filterEmptyGroups = edges => filter(edges, groupHasPeople);
const groupIsPrivateNoAccess = edge => {
  return (
    !edge.node.isPrivate.value ||
    (groupHasPeople(edge) && edge.node.people.value.edges[0].node.userRole.value !== 'No Access')
  );
};
const filterNoAccessPrivateGroups = edges => filter(edges, groupIsPrivateNoAccess);

const toPrivateGroupLabel = (name: null | string, email: string) => (name ? `${name} (${email})` : email);
const toGroupLabel = (title: string) => `${title} (group)`;

const parsePrivateGroup = edge => {
  const person = edge.node.people.value.edges[0];

  const id = edge.node.id;
  const name = person.node.name && person.node.name.value;
  const email = person.node.email && person.node.email.value;

  return {
    id,
    value: toPrivateGroupLabel(name, email),
    label: toPrivateGroupLabel(name, email),
  };
};
const parseGroup = edge => {
  const id = edge.node.id;
  const title = edge.node.title.value;
  return {
    id,
    value: id,
    label: toGroupLabel(title),
  };
};

const toOptionValue = edge => {
  const isPrivate: boolean = edge.node.isPrivate.value;
  const parseFn = isPrivate ? parsePrivateGroup : parseGroup;
  return parseFn(edge);
};

const mapToOptionValue = edges => map(edges, toOptionValue);

const toDropdownOptionsEdit = flow(
  groups => filterEmptyGroups(groups),
  groups => mapToOptionValue(groups)
);

const toDropdownOptions = flow(
  groups => filterEmptyGroups(groups),
  // Don't show private groups for No Access users
  groups => filterNoAccessPrivateGroups(groups),
  groups => mapToOptionValue(groups)
);

export const groupDropdownExtraInfo = {
  optionsQuery: groupQuery,
  optionsQueryFilter: groupDropdownOptionsQueryFilter,
  dropdownOptionsParser: data => {
    const edges = get(data, 'dropdownOptions.edges', []);

    return toDropdownOptions(edges);
  },
  editValueParser: data => {
    const edges = get(data, 'value.edges', []);

    return toDropdownOptionsEdit(edges);
  },
  submissionDataParser: flatByAttribute('id'),
};

export function createVuelidateObject(fields: FormField[]): ValidationOptions {
  return fields.reduce((formValidations: ValidationOptions, field: FormField): ValidationOptions => {
    if (!field.validation) return formValidations;

    const fieldValidations = field.validation.reduce((fieldValidations, validation) => {
      const validatorType = camelCase(validation.validator);

      if (!validators[validatorType]) return fieldValidations;

      const validatorFunction = validation.meta.length
        ? validators[validatorType](validation.meta, field)
        : validators[validatorType];

      return {
        ...fieldValidations,
        [validatorType]: validatorFunction,
      };
    }, {});

    return { ...formValidations, [field.name]: fieldValidations };
  }, {});
}

const replaceRawValidOptionsKeys = (rawOption: RawValidOption): ValidOption => ({
  value: rawOption.title,
  label: rawOption.human,
});

function normalizeNewObjectAttributes(attributes: RawObjectAttribute[]): ObjectAttribute[] {
  return attributes.reduce(
    (attributesAccumulator: ObjectAttribute[], currentAttribute: RawObjectAttribute): ObjectAttribute[] => {
      if (currentAttribute.sorCreate) {
        const validOptions = currentAttribute.validOptions.map(replaceRawValidOptionsKeys);

        const normalizedAttribute = { ...currentAttribute, validOptions };

        return attributesAccumulator.concat(normalizedAttribute);
      }
      return attributesAccumulator;
    },
    []
  );
}

export function normalizeNewObject(newObject: RawZenObject): ZenObject {
  const normalizedAttributes = normalizeNewObjectAttributes(newObject.attrs);
  return { ...newObject, attrs: normalizedAttributes };
}

export function hasPresenter(objectAttribute: ObjectAttribute, attrMapper: Mapper): boolean {
  const mapper = attrMapper[objectAttribute.name];
  const hasPresenter = !!mapper || objectAttribute.isCustomAttribute;

  return hasPresenter;
}

export function enumerateArrayElements(arr: string[]): string {
  let outStr = '';
  if (arr.length === 1) {
    outStr = arr[0];
  } else if (arr.length === 2) {
    outStr = arr.join(' and ');
  } else if (arr.length > 2) {
    outStr = arr.slice(0, -1).join(', ') + ' and ' + arr.slice(-1);
  }
  return outStr;
}

export function formatStringDate(stringDate: string, stringFormat = DATE_FORMAT): string {
  if (!stringDate) return '';
  const date = parseISO(stringDate);
  return format(date, stringFormat);
}
